import { runtimeEnv } from '@/lib/runtime-config';
import { z } from 'zod';

/**
 * Public configuration of the Tahiti client: the `VITE_*` variables of `.env.example`,
 * validated with Zod when the application starts.
 *
 * They are read through `runtimeEnv()`, when the application starts and never earlier. In
 * a container they come from the `/config.js` that the container writes from its own
 * `VITE_*` variables when it starts, which is what lets ONE image serve every environment
 * (the identifiers differ between Tahiti environments). Anywhere else (vite dev, vite
 * preview, the tests) nothing writes that file and they come from `import.meta.env`, as
 * in any Vite app.
 *
 * There is NO default value in the code: Tahiti forbids carrying identifiers from one
 * environment to another, so a missing variable stops the application on the
 * configuration error screen (`components/config/`) instead of falling back to a value
 * that could belong to another environment. Every variable here is public, readable by
 * any browser (in the bundle, or in `/config.js`): never a service token or an
 * `api_token`.
 */

/** A variable that is empty is a variable that is absent (`VITE_X=` in a `.env` file). */
const vide = (valeur: unknown): unknown =>
  typeof valeur === 'string' && valeur.trim() === '' ? undefined : valeur;

const requise = (message: string) =>
  z
    .string({ error: (issue) => (issue.input === undefined ? message : undefined) })
    .trim()
    .min(1, message);

/**
 * Where a Tahiti service lives, from the point of view of the browser: a full http(s) URL
 * (a host that answers CORS), or an address relative to the origin of the page, starting
 * with `/`, which the relay of the server (`/api`, see vite.config.ts and
 * nginx/api.locations.template) forwards to Tahiti. `//host` is refused: it is a URL in
 * disguise, on another origin, which is precisely what the relay exists to avoid.
 */
const urlSchema = z.preprocess(
  vide,
  requise('absente').refine((valeur) => {
    if (valeur.startsWith('/')) return !valeur.startsWith('//');
    try {
      const { protocol } = new URL(valeur);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  }, "doit être une URL http ou https complète, ou une adresse relative à l'origine commençant par « / » (ex. /api/auth)"),
);

const identifiantSchema = z.preprocess(
  vide,
  requise('absente').regex(/^\d+$/, 'doit être un entier positif (identifiant numérique)'),
);

const texteSchema = z.preprocess(vide, requise('absente'));

/** The raw variables, as Vite exposes them (strings, possibly empty or absent). */
export const variablesConfigSchema = z.object({
  VITE_AUTH_SERVICE_URL: urlSchema,
  VITE_H2DATA_URL: urlSchema,
  VITE_ROSETTA_WORK_CONTEXT_ID: z.preprocess(vide, z.string().trim().min(1).optional()),
  VITE_ROSETTA_PROJECT_ID: identifiantSchema,
  VITE_ROSETTA_ACTIVITY_ID: identifiantSchema,
  VITE_ROSETTA_CATEGORY: texteSchema,
  VITE_ROSETTA_WORKSPACE_ID: identifiantSchema,
});

/** The names of the variables, in the order of `.env.example`. */
export const NOMS_VARIABLES = Object.keys(variablesConfigSchema.shape);

const sansBarreFinale = (url: string): string => url.replace(/\/+$/, '');

/** What the Tahiti client needs, typed (identifiers are numbers, URLs have no trailing slash). */
export interface ConfigTahiti {
  /**
   * Authentication service, without a trailing slash: an absolute URL, or an address
   * relative to the page origin (`/api/auth`, so the login goes to `/api/auth/login`).
   */
  readonly urlAuth: string;
  /**
   * H2Data and TaskService, without a trailing slash: an absolute URL, or empty when the
   * variable is `/` (the page origin itself: the client appends `/api/v2/...`, which is
   * exactly the path the relay forwards).
   */
  readonly urlH2data: string;
  /**
   * Work context to select after login. Optional: with a single context the client selects
   * it on its own; with several, this one is required.
   */
  readonly contexteTravailId?: string;
  /** `h2_project_id` */
  readonly projetId: number;
  /** `h2_activity_id` */
  readonly activiteId: number;
  /** `category` */
  readonly categorie: string;
  /** `h2_workspace_id` */
  readonly espaceTravailId: number;
}

export interface ProblemeConfig {
  /** Name of the variable at fault. */
  readonly variable: string;
  /** What is wrong with it, in French (shown on the configuration error screen). */
  readonly message: string;
}

export type ResultatConfig =
  | { readonly ok: true; readonly config: ConfigTahiti }
  | { readonly ok: false; readonly problemes: readonly ProblemeConfig[] };

/** Raised when something asks for the client while the configuration is invalid. */
export class ConfigurationInvalideError extends Error {
  override name = 'ConfigurationInvalideError';

  constructor(public readonly problemes: readonly ProblemeConfig[]) {
    super(
      `Invalid configuration: ${problemes.map((p) => `${p.variable} (${p.message})`).join(', ')}`,
    );
  }
}

/**
 * Validates the public variables. `env` is `runtimeEnv()` unless a test brings its own:
 * the `VITE_*` values the container wrote into `/config.js` when it started, over
 * `import.meta.env` (which is all there is outside a container). It is read on every
 * call, never when the module loads. Never throws: the caller decides between the
 * application and the error screen.
 */
export function chargerConfiguration(
  env: Readonly<Record<string, unknown>> = runtimeEnv(),
): ResultatConfig {
  const resultat = variablesConfigSchema.safeParse(env);
  if (!resultat.success) {
    return {
      ok: false,
      problemes: resultat.error.issues.map((issue) => ({
        variable: String(issue.path[0] ?? ''),
        message: issue.message,
      })),
    };
  }

  const v = resultat.data;
  return {
    ok: true,
    config: {
      urlAuth: sansBarreFinale(v.VITE_AUTH_SERVICE_URL),
      urlH2data: sansBarreFinale(v.VITE_H2DATA_URL),
      ...(v.VITE_ROSETTA_WORK_CONTEXT_ID === undefined
        ? {}
        : { contexteTravailId: v.VITE_ROSETTA_WORK_CONTEXT_ID }),
      projetId: Number(v.VITE_ROSETTA_PROJECT_ID),
      activiteId: Number(v.VITE_ROSETTA_ACTIVITY_ID),
      categorie: v.VITE_ROSETTA_CATEGORY,
      espaceTravailId: Number(v.VITE_ROSETTA_WORKSPACE_ID),
    },
  };
}

/** The valid configuration (`env` as in `chargerConfiguration`), or `ConfigurationInvalideError`. */
export function configurationValide(
  env: Readonly<Record<string, unknown>> = runtimeEnv(),
): ConfigTahiti {
  const resultat = chargerConfiguration(env);
  if (!resultat.ok) throw new ConfigurationInvalideError(resultat.problemes);
  return resultat.config;
}
